import {tvScreenTexture} from './tv-screen';
import {artShape,artTexture,wovenMaterial,fixtureModel,ART_STYLES} from './decor-design';
import {roomArtworkTexture} from './room-artwork';
import {createDrawerFace} from '../design/configurator/room/drawer-face';
import {createOakSoffitMaterial} from './oak-soffit-material';
import * as T from 'three';
import {bunkAccessZones,footprint,placeClearFurnishings} from './room-furnishing-clearance';
import {disposeCameraBunk} from './camera-model';
import {roomAnchorPosition,roomFloorDepth,type RoomWalls,type WallCamera} from './photo-walls';
import {furnishingSize,normalizeFurnishing,isWallFurnishing,type Furnishing} from './room-furnishings';
const INCH=.0254;
/** Illustrative accessories in inches, converted once to the room's meter units. */
export function createFurnishingModel(input:Furnishing,ceilingInches=108){
 const item=normalizeFurnishing(input,ceilingInches),d=furnishingSize(item),group=new T.Group();
 const paint=item.kind==='dresser'&&item.color==='#ebdabd'?createOakSoffitMaterial():new T.MeshStandardMaterial({color:item.color,roughness:.8});
 if(paint instanceof T.MeshPhysicalMaterial){paint.emissiveIntensity=0;paint.roughness=.65;paint.clearcoat=.12;paint.clearcoatRoughness=.6;}

 const dark=new T.MeshStandardMaterial({color:'#303433',roughness:.55});
 const box=(w:number,h:number,depth:number,x:number,y:number,z:number,material=paint)=>{
  const mesh=new T.Mesh(new T.BoxGeometry(w,h,depth),material);mesh.position.set(x,y,z);group.add(mesh);return mesh;
 };
 if(['wallLight','ceilingFan','mirror'].includes(item.kind)){group.add(fixtureModel(item,ceilingInches));
 }else if(item.kind==='rug'){
  if(item.color==='#f7f4ed'){
   // Dense ivory-white tufts, with a soft uneven pile rather than a flat color patch.
   const pile=new T.InstancedMesh(new T.SphereGeometry(1,6,4),new T.MeshStandardMaterial({color:'#fffefa',roughness:1}),2600);
   const matrix=new T.Matrix4(),q=new T.Quaternion(),scale=new T.Vector3(),position=new T.Vector3();
   const random=(n:number)=>{const v=Math.sin(n*127.1+31.7)*43758.5453;return v-Math.floor(v);};
   for(let i=0;i<2600;i++){
    const x=(random(i*3)-.5)*(d.width-1),z=(random(i*3+1)-.5)*(d.depth-1),height=.5+random(i*3+2)*.8;
    position.set(x,.3+height*.45,z);scale.set(.3,height,.3);q.setFromEuler(new T.Euler((random(i+9)-.5)*.5,0,(random(i+19)-.5)*.5));matrix.compose(position,q,scale);pile.setMatrixAt(i,matrix);
   }
   // Place the complete rotated tuft bounds on the backing, not below the floor.
   pile.computeBoundingBox();
   pile.position.y=d.height-pile.boundingBox!.min.y;
   const pileBounds=pile.boundingBox!;
   pile.scale.x=Math.min(1,d.width/(2*Math.max(Math.abs(pileBounds.min.x),Math.abs(pileBounds.max.x))));
   pile.scale.z=Math.min(1,d.depth/(2*Math.max(Math.abs(pileBounds.min.z),Math.abs(pileBounds.max.z))));
   pile.name='Polar white shag pile';group.add(pile);
  }
  box(d.width,d.height,d.depth,0,d.height/2,0,wovenMaterial(item.color));
  const border=new T.MeshStandardMaterial({color:new T.Color(item.color).multiplyScalar(.72),roughness:1});
  for(const side of [-1,1]){
   box(d.width-3,.04,1,0,d.height+.02,side*(d.depth/2-2),border);
   box(1,.04,d.depth-3,side*(d.width/2-2),d.height+.02,0,border);
  }
 }else if(item.kind==='dresser'){
  box(d.width,d.height-4,d.depth,0,(d.height+4)/2,0);
  for(const x of [-1,1])for(const z of [-1,1])box(2,4,2,x*(d.width/2-2),2,z*(d.depth/2-2));
  const rows=d.height>=50?5:3,columns=d.width>=60?2:1;
  const frontColor=item.drawerColor??item.color;
  const frontPaint=frontColor==='#ebdabd'?createOakSoffitMaterial():new T.MeshStandardMaterial({color:frontColor,roughness:.65});
  if(frontPaint instanceof T.MeshPhysicalMaterial){frontPaint.emissiveIntensity=0;frontPaint.roughness=.65;frontPaint.clearcoat=.12;}
  for(let row=0;row<rows;row++)for(let column=0;column<columns;column++){
   const y=4+(row+.5)*(d.height-5)/rows,height=(d.height-5)/rows-.35,width=(d.width-2-(columns-1)*.5)/columns;
   const face=createDrawerFace(width,height,item.dresserStyle==='nickel_gap'?'shiplap':'shaker','silver_bar');
   face.scale.setScalar(12);face.position.set(-(d.width-2)/2+column*(width+.5),y-height/2,d.depth/2+.05);
   face.traverse(object=>{if(!(object instanceof T.Mesh)||!object.userData.finishPart)return;
    const original=object.material as T.Material;original.dispose();
    if(object.userData.finishShade){const shaded=frontPaint.clone();shaded.color.multiplyScalar(object.userData.finishShade);object.material=shaded;}else object.material=frontPaint;
   });group.add(face);
  }
 }else if(item.kind==='beanbag'){
  // A low, filled fabric sack: flattened base, broad belly and a scooped seat.
  // Sculpt a continuous surface so the back and seat are one soft form.
  const geometry=new T.SphereGeometry(1,64,40);
  const positions=geometry.attributes.position;
  for(let i=0;i<positions.count;i++){
   const x=positions.getX(i),y=positions.getY(i),z=positions.getZ(i);
   const theta=Math.atan2(z,x);
   const folds=1+.025*Math.sin(theta*7+y*5)+.014*Math.sin(theta*13-y*8);
   const fullness=1+.16*(1-Math.abs(y));
   const px=x*d.width/2*fullness*folds;
   const pz=z*d.depth/2*fullness*folds;
   let py=(y+1)*d.height*.40;
   if(y>0){
    // The rear rises while the front lip slumps and the center sinks.
    const back=Math.max(0,-z);
    const seat=Math.exp(-((x/.58)**2+((z-.16)/.62)**2));
    py+=d.height*(.25*back-.32*seat-.13*Math.max(0,z))*y;
   }
   py=Math.max(d.height*.025,py);
   positions.setXYZ(i,px,py,pz);
  }
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  const bounds=geometry.boundingBox!;
  const size=bounds.getSize(new T.Vector3());
  geometry.translate(-(bounds.min.x+bounds.max.x)/2,-bounds.min.y,-(bounds.min.z+bounds.max.z)/2);
  geometry.scale(d.width/size.x,d.height/size.y,d.depth/size.z);
  const fabric=wovenMaterial(item.color);
  const mesh=new T.Mesh(geometry,fabric);mesh.name='Soft bean bag';group.add(mesh);
 }else if(item.kind==='painting'){
  // Preserve the artwork silhouette: no rectangular backing behind its alpha.
  if(item.frameStyle){
   const frame=new T.Mesh(new T.RingGeometry(d.width/2-.15,d.width/2+.8,96),new T.MeshStandardMaterial({color:['#ffffff','#ebdabd','#f4f0e8','#171c20'][item.frameStyle%4],side:T.DoubleSide}));
   frame.position.set(0,d.height/2,d.depth/2);frame.name='Artwork frame';group.add(frame);
  }
  const style=item.artStyle??0,shape=artShape(style);
  const map=style===0?roomArtworkTexture():artTexture(style);
  const material=new T.MeshBasicMaterial({map,color:map?0xffffff:0xd4e5ed,transparent:true,alphaTest:.02,depthWrite:false,side:T.DoubleSide});
  if(map)material.addEventListener('dispose',()=>map.dispose());
  const art=new T.Mesh(style===0?new T.PlaneGeometry(d.width,d.height):new T.ShapeGeometry(shape.shape),material);art.position.set(0,style===0?d.height/2:0,d.depth/2+.02);art.name=ART_STYLES[style];if(style!==0){const uv=art.geometry.getAttribute('uv');for(let i=0;i<uv.count;i++)uv.setXY(i,(uv.getX(i)+shape.width/2)/shape.width,uv.getY(i)/shape.height);uv.needsUpdate=true;}group.add(art);
 }else{
  box(d.width,d.height,d.depth,0,d.height/2,0,paint);
  const map=tvScreenTexture();
  const screen=new T.MeshBasicMaterial({map,color:map?'#ffffff':'#233b49'});
  if(map)screen.addEventListener('dispose',()=>map.dispose());
  box(d.width-1,d.height-1,.08,0,d.height/2,d.depth/2+.06,screen);
 }
 group.scale.setScalar(INCH);
 if(isWallFurnishing(item)){
  group.rotation.y=item.wall==='left'?Math.PI/2:0;
  group.position.set((item.wall==='left'?d.depth/2+.5:item.x)*INCH,item.bottom*INCH,(item.wall==='right'?d.depth/2+.5:item.z)*INCH);
 }else{group.position.set(item.x*INCH,item.kind==='ceilingFan'?item.bottom*INCH:0,item.z*INCH);group.rotation.y=item.turn*Math.PI/180;}
 group.userData.furnishingId=item.id;return group;
}
export function roomFurnishings(items:readonly Furnishing[],walls:RoomWalls,calibration:WallCamera,aspect:number,ceilingInches:number,bunks:readonly T.Object3D[]=[],constrainToPhoto=true){
 const depth=roomFloorDepth(walls.corner,aspect,calibration,ceilingInches);if(depth===null)return null;
 const group=new T.Group();group.quaternion.setFromRotationMatrix(calibration.basis);
 group.position.copy(roomAnchorPosition(walls.corner,aspect,calibration.focal,depth));
 group.updateMatrixWorld(true);
 const camera=new T.PerspectiveCamera(2*Math.atan(.5/calibration.focal)*180/Math.PI,aspect,.01,200);camera.updateMatrixWorld();
 const zones=bunkAccessZones(bunks,group.matrixWorld);
 // If geometry is unavailable, omit decor instead of guessing a clear location.
 const valid=zones.every(zone=>Object.values(zone).every(Number.isFinite));
 const resolved=valid?placeClearFurnishings(items.map(item=>normalizeFurnishing(item,ceilingInches)),zones,item=>{
  const model=createFurnishingModel(item,ceilingInches);model.updateMatrixWorld(true);
  const box=new T.Box3().setFromObject(model);box.min.divideScalar(.0254);box.max.divideScalar(.0254);
  disposeCameraBunk(model);return footprint(box);
 },(x,z)=>{
  const point=new T.Vector3(x*.0254,0,z*.0254).applyMatrix4(group.matrixWorld).project(camera);
  return !constrainToPhoto||(point.z>-1&&point.z<1&&point.x>=-1&&point.x<=1&&point.y>=-1&&point.y<=1);
 },ceilingInches):[];
 group.userData.resolvedFurnishings=resolved;
 for(const item of resolved)group.add(createFurnishingModel(item,ceilingInches));
 return group;
}


/** Cycle curated placement requests through the same clearance solver as initial decor. */
export function nextDecorPlacement(items:readonly Furnishing[],id:string,walls:RoomWalls,calibration:WallCamera,aspect:number,ceilingInches:number,bunks:readonly T.Object3D[]=[]){
 const item=items.find(item=>item.id===id);if(!item)return [...items];
 const options:Partial<Furnishing>[]=isWallFurnishing(item)
  ?[...['right','left'] as const].flatMap(wall=>[72,120,168,216].map(n=>({wall,x:n,z:n})))
  :item.kind==='dresser'
  ?[0,90].flatMap(turn=>[72,120,168,216].map(n=>turn===90?{turn,x:furnishingSize(item).depth/2+1,z:n}:{turn,x:n,z:furnishingSize(item).depth/2+1}))
  :item.kind==='beanbag'
  // A fixed room-space ring provides nearby alternatives when the four broad
  // composition slots are blocked. Absolute slots cannot drift with each click.
  ?Array.from({length:8},(_,row)=>Array.from({length:8},(_,column)=>({x:36+(row%2?7-column:column)*24,z:36+row*24,turn:item.turn}))).flat()
  :[{x:84,z:120,turn:0},{x:144,z:84,turn:90},{x:180,z:144,turn:0},{x:84,z:180,turn:90}];
 // Advance around the fixed option ring instead of toggling between two spots.
 const distance=(option:Partial<Furnishing>)=>(option.x!-item.x)**2+(option.z!-item.z)**2+((option.wall&&option.wall!==item.wall)||(option.turn!==undefined&&option.turn!==item.turn)?100000:0);
 const current=options.reduce((best,option,index)=>distance(option)<distance(options[best])?index:best,0);
 const candidates=Array.from({length:options.length},(_,index)=>options[(current+index+1)%options.length]);
 for(const candidate of candidates){
  const proposed=normalizeFurnishing({...item,...candidate},ceilingInches);
  const group=roomFurnishings([...items.filter(other=>other.id!==id),proposed],walls,calibration,aspect,ceilingInches,bunks,false);
  if(!group)continue;
  const resolved=group.userData.resolvedFurnishings as Furnishing[];
  disposeCameraBunk(group);
  const moved=resolved.find(other=>other.id===id);
  if(!moved||resolved.length!==items.length)continue;
  // A click selects a fixed slot, never the solver's progressively displaced fallback.
  // Wall-mounted items use only their along-wall coordinate.
  const sameSlot=isWallFurnishing(proposed)
   ?moved.wall===proposed.wall&&Math.abs((proposed.wall==='left'?moved.z:moved.x)-(proposed.wall==='left'?proposed.z:proposed.x))<.01
   :Math.hypot(moved.x-proposed.x,moved.z-proposed.z)<.01&&moved.turn===proposed.turn;
  if(!sameSlot)continue;
  if(items.some(other=>other.id!==id&&JSON.stringify(other)!==JSON.stringify(resolved.find(result=>result.id===other.id))))continue;
  if(moved.wall===item.wall&&Math.hypot(moved.x-item.x,moved.z-item.z)<12&&moved.turn===item.turn)continue;
  return items.map(other=>other.id===id?moved:other);
 }
 return [...items];
}
